/**
 * Loads Jarvis client scripts from CDN
 * Based on Adobe Jarvis documentation
 */

import { CONFIG } from '@frameio/config';

const PRECONNECT_ID = 'jarvis-preconnect';

const DEFAULT_CDN_BASE = 'https://stage-client.messaging.adobe.com';

function getCdnBase(): string {
  return CONFIG.thirdParty.jarvis?.cdnBase ?? DEFAULT_CDN_BASE;
}

function addPreconnect(): void {
  if (document.getElementById(PRECONNECT_ID)) return;
  const baseUrl = getCdnBase();
  const link = document.createElement('link');
  link.id = PRECONNECT_ID;
  link.rel = 'preconnect';
  link.href = baseUrl;
  link.crossOrigin = 'anonymous';
  document.head.appendChild(link);
}

export type LoadJarvisOptions = {
  version?: string;
  onLoad?: () => void;
  onError?: (error: Error) => void;
};

/**
 * Loads Jarvis client JavaScript and CSS files
 */
export function loadJarvisScripts(
  options: LoadJarvisOptions = {}
): Promise<void> {
  const { version = 'latest', onLoad, onError } = options;

  addPreconnect();

  const baseUrl = getCdnBase();
  const jsUrl = `${baseUrl}/${version}/AdobeMessagingClient.js`;
  const cssUrl = `${baseUrl}/${version}/AdobeMessagingClient.css`;

  return new Promise((resolve, reject) => {
    if (window.AdobeMessagingExperienceClient) {
      onLoad?.();
      resolve();
      return;
    }

    const linkId = 'jarvis-css';
    if (!document.getElementById(linkId)) {
      const link = document.createElement('link');
      link.id = linkId;
      link.rel = 'stylesheet';
      link.href = cssUrl;
      link.crossOrigin = 'anonymous';
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = jsUrl;
    script.crossOrigin = 'anonymous';
    script.async = true;

    script.onload = () => {
      onLoad?.();
      resolve();
    };

    script.onerror = () => {
      const error = new Error(`Failed to load Jarvis client from ${jsUrl}`);
      onError?.(error);
      reject(error);
    };

    document.head.appendChild(script);
  });
}
